import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, Loader2 } from "lucide-react";
import { auth } from "@/lib/firebase";
import { workspaceKeysApi } from "@/lib/api/workspace-keys";
import toast from "react-hot-toast";

interface RevokeKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyId: string;
  onSuccess: () => void;
}

export default function RevokeKeyModal({
  isOpen,
  onClose,
  keyId,
  onSuccess,
}: RevokeKeyModalProps) {
  const [reason, setReason] = useState("");
  const [revoking, setRevoking] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen]);

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      await workspaceKeysApi.revoke(token, keyId, reason.trim() || undefined);
      toast.success("API key revoked successfully");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Failed to revoke key:", error);
      toast.error(error.response?.data?.message || "Failed to revoke key");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] w-full max-w-[90vw] rounded-lg">
        <DialogHeader>
          <DialogTitle>Revoke API Key</DialogTitle>
          <DialogDescription>
            This action cannot be undone. The key will be immediately
            deactivated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="border-red-500/20 bg-red-50/50 dark:bg-red-900/10">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">
                    Warning
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    This action cannot be undone. SmallApp cache may take up to
                    1 hour to expire.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label htmlFor="keyId">Key ID</Label>
            <code className="block text-sm font-mono bg-muted px-3 py-2 rounded border">
              {keyId}
            </code>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason <span className="text-muted-foreground">(optional)</span>
            </Label>
            <textarea
              id="reason"
              placeholder="e.g., Key compromised - rotating to new key"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={revoking}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {revoking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                "Confirm Revocation"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
